import { useEffect, useState } from "react";
import { Menu, X, Flame } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import Logo from "@/components/landing/Logo";

const links = [
    { label: "La Méthode", href: "/#methode", internal: false },
    { label: "Experts", href: "/#experts", internal: false },
    { label: "Articles", to: "/articles", internal: true },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="site-navbar"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
                scrolled ? "bg-white/85 backdrop-blur-xl border-b border-ink-100" : "bg-transparent"
            }`}
        >
            <nav className="container-wide flex items-center justify-between h-24">
                <Logo />

                <ul className="hidden lg:flex items-center gap-8">
                    {links.map((l) =>
                        l.internal ? (
                            <li key={l.to}>
                                <Link
                                    to={l.to}
                                    data-testid={`nav-link-${l.to.replace(/\//g, "")}`}
                                    className="text-sm font-medium text-ink-700 hover:text-brand-600 transition-colors"
                                >
                                    {l.label}
                                </Link>
                            </li>
                        ) : (
                            <li key={l.href}>
                                <a
                                    data-testid={`nav-link-${l.href.replace(/[/#]/g, "")}`}
                                    href={l.href}
                                    className="text-sm font-medium text-ink-700 hover:text-brand-600 transition-colors"
                                >
                                    {l.label}
                                </a>
                            </li>
                        )
                    )}
                </ul>

                <div className="hidden lg:flex items-center gap-2">
                    <Link to="/diagnostic" data-testid="nav-diagnostic-link" className="btn-ghost text-sm">
                        Diagnostic offert
                    </Link>
                    <Link to="/test" data-testid="nav-test-cta" className="btn-primary text-sm">
                        Faire le test
                    </Link>
                </div>

                <button
                    data-testid="nav-mobile-toggle"
                    onClick={() => setOpen((v) => !v)}
                    className="lg:hidden h-10 w-10 grid place-items-center rounded-2xl border border-ink-200 bg-white"
                    aria-label="Menu"
                >
                    {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
                </button>
            </nav>

            {open && (
                <div data-testid="mobile-menu" className="lg:hidden bg-white border-t border-ink-100">
                    <ul className="container-wide py-6 flex flex-col gap-3">
                        {links.map((l) =>
                            l.internal ? (
                                <li key={l.to}>
                                    <Link
                                        onClick={() => setOpen(false)}
                                        to={l.to}
                                        data-testid={`nav-mobile-link-${l.to.replace(/\//g, "")}`}
                                        className="block py-2 text-base font-medium text-ink-800"
                                    >
                                        {l.label}
                                    </Link>
                                </li>
                            ) : (
                                <li key={l.href}>
                                    <a
                                        onClick={() => setOpen(false)}
                                        href={l.href}
                                        data-testid={`nav-mobile-link-${l.href.replace(/[/#]/g, "")}`}
                                        className="block py-2 text-base font-medium text-ink-800"
                                    >
                                        {l.label}
                                    </a>
                                </li>
                            )
                        )}
                        <li className="pt-2 flex gap-2">
                            <button
                                onClick={() => {
                                    setOpen(false);
                                    navigate("/diagnostic");
                                }}
                                className="btn-outline flex-1 text-sm"
                                data-testid="nav-mobile-diagnostic"
                            >
                                Diagnostic
                            </button>
                            <button
                                onClick={() => {
                                    setOpen(false);
                                    navigate("/test");
                                }}
                                className="btn-primary flex-1 text-sm"
                                data-testid="nav-mobile-test"
                            >
                                Faire le test
                            </button>
                        </li>
                    </ul>
                </div>
            )}
        </header>
    );
}
